import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, ArrowRight, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CY = '#90E0EF';
const SYM = { GBP: '£', EUR: '€', USD: '$', PLN: 'zł' };

function money(minor, cur = 'GBP') {
  const v = (Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur === 'PLN' ? `${v} ${SYM[cur]}` : `${SYM[cur] || ''}${v}`;
}

function Shell({ children }) {
  return (
    <div className="min-h-[100svh] bg-[#050505] text-[#f0f0f0] relative overflow-hidden" data-testid="public-payment-page">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
      <div className="relative z-10 max-w-md mx-auto px-6 py-10 sm:py-16">{children}</div>
    </div>
  );
}

function Label({ children }) {
  return <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">{children}</p>;
}

export default function PublicPaymentPage() {
  const { publicCode } = useParams();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/sales/${publicCode}`)
      .then((r) => { setSale(r.data); setName(r.data.buyer_name || ''); setEmail(r.data.buyer_email || ''); })
      .catch((e) => setError(e?.response?.data?.detail || 'This payment link could not be found.'))
      .finally(() => setLoading(false));
  }, [publicCode]);

  const isDone = useMemo(() => sale && ['paid', 'cancelled', 'expired', 'refunded'].includes(sale.status), [sale]);

  async function pay() {
    if (!acceptTerms) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/sales/${publicCode}/checkout`, {
        origin_url: window.location.origin,
        accept_terms: true,
        marketing_opt_in: marketing,
        buyer_name: name || undefined,
        buyer_email: email || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start the payment. Please try again.');
      setSubmitting(false);
    }
  }

  if (loading) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" style={{ color: CY }} /></div></Shell>;
  if (error && !sale) return <Shell><div className="text-center py-16"><p className="font-outfit text-xl text-white">Link unavailable</p><p className="text-xs font-mono text-[#888] mt-3">{error}</p></div></Shell>;

  return (
    <Shell>
      {/* identity */}
      <div className="flex items-center gap-2.5 mb-10">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
        <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CY }}>{sale.seller_label}</span>
      </div>

      <Label>{sale.sale_type === 'card' ? `Trading Card #${sale.card_number_display}` : 'Acquisition'}</Label>
      <h1 className="font-outfit font-bold text-white tracking-tight mt-2" style={{ fontSize: '2.25rem', lineHeight: 1.05 }} data-testid="pay-item-title">{sale.item_title}</h1>
      {sale.artist_name && <p className="text-sm font-mono text-[#bbb] mt-2">{sale.artist_name}</p>}
      {sale.edition && <p className="text-xs font-mono text-[#777] mt-1 tracking-[0.1em]">EDITION {sale.edition}</p>}

      <div className="my-8 border-t border-b border-[#1a1a1a] py-6">
        <Label>Price</Label>
        <p className="font-outfit font-bold text-white mt-1" style={{ fontSize: '3rem', lineHeight: 1 }} data-testid="pay-amount">{money(sale.amount_minor, sale.currency)}</p>
      </div>

      {isDone ? (
        <div className="border border-[#1a1a1a] px-5 py-6 text-center" data-testid="pay-closed-state">
          {sale.status === 'paid' ? (
            <>
              <p className="flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-[0.2em]" style={{ color: '#7ed4a0' }}><Check className="w-4 h-4" /> Payment Confirmed</p>
              <p className="text-xs font-mono text-[#888] mt-3">Thank you. This artwork has been secured.</p>
            </>
          ) : (
            <p className="text-sm font-mono text-[#888] uppercase tracking-[0.16em]">This payment link is no longer active ({sale.status}).</p>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-6">
            <div>
              <Label>Buyer</Label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="pay-name"
                className="w-full bg-transparent border-b border-[#222] text-[#f0f0f0] text-base py-2 mt-2 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#555]" />
            </div>
            <div>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" data-testid="pay-email"
                className="w-full bg-transparent border-b border-[#222] text-[#f0f0f0] text-base py-2 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#555]" />
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer mb-3" data-testid="pay-terms-label">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} data-testid="pay-terms-checkbox" className="mt-1 accent-[#90E0EF] w-4 h-4" />
            <span className="text-xs font-mono text-[#c8c8c8] leading-relaxed">
              I accept the <a href="https://therelativegallery.com" target="_blank" rel="noreferrer" className="underline" style={{ color: CY }}>Terms &amp; Conditions</a> and the <a href="https://therelativegallery.com" target="_blank" rel="noreferrer" className="underline" style={{ color: CY }}>Privacy Policy</a>.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer mb-8" data-testid="pay-marketing-label">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="pay-marketing-checkbox" className="mt-1 accent-[#90E0EF] w-4 h-4" />
            <span className="text-xs font-mono text-[#8a8a8a] leading-relaxed">
              Keep me updated about exhibitions, artists and gallery events by email. Optional.
            </span>
          </label>

          {error && <p className="text-xs font-mono text-[#e88ba0] mb-4">{error}</p>}

          <button onClick={pay} disabled={!acceptTerms || submitting} data-testid="pay-continue-btn"
            style={{ cursor: acceptTerms && !submitting ? 'pointer' : 'not-allowed', background: acceptTerms ? CY : 'transparent', color: acceptTerms ? '#050505' : '#666', border: `1px solid ${acceptTerms ? CY : '#2a2a2a'}` }}
            className="w-full flex items-center justify-center gap-2 py-4 text-[11px] font-mono uppercase tracking-[0.22em] transition-all duration-200 disabled:opacity-60">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><Lock className="w-3.5 h-3.5" /> Continue to Payment <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[10px] font-mono text-[#555] text-center mt-4 tracking-[0.1em]">Secured by Stripe · Apple Pay · Google Pay · Card</p>
        </>
      )}
    </Shell>
  );
}
